import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Button, Label } from '@treams/treams-ui';

import { WithID } from '../../../../../src/packages/kakapo-ui-services/services/common/ListService';
import { tokens } from '../theme/tokens';

const DisabledText = styled.span`
	color: ${tokens.text.primary};
	font-weight: 700;
`;

export const ActionListItem = ({
	label,
	buttonTitle,
	buttonIndication,
	buttonDisabled,
	onButtonClick,
	disabledText,
}: {
	label: string;
	buttonTitle: string;
	buttonIndication?: 'destructive' | 'secondary';
	buttonDisabled?: boolean;
	onButtonClick: (event: React.MouseEvent) => void;
	disabledText?: string | null;
}) => {
	return (
		<Flex mb={3} alignItems="center">
			<Box mr={2} width={5 / 12} flexGrow={1}>
				<Label>{label}</Label>
				{buttonDisabled && disabledText && <DisabledText>{disabledText}</DisabledText>}
			</Box>
			<Box>
				<Button
					type={buttonIndication === 'destructive' ? 'destructive' : 'secondary'}
					small
					onClick={onButtonClick}
					disabled={buttonDisabled}
					title={buttonTitle}
				/>
			</Box>
		</Flex>
	);
};

interface IArchiveable {
	isArchived: boolean;
}

interface IArchiveableInput {
	variables: {
		input: {
			id: string;
		};
	};
}

export const ActionArchiveListItem = <T extends WithID & IArchiveable>({
	archivableItem,
	archivableItemName,
	useArchive,
	useRestore,
	buttonDisabled,
}: {
	archivableItem: T;
	archivableItemName: string;
	archivableItemKey: string;
	useArchive: () => [(input: IArchiveableInput) => void, object];
	useRestore: () => [(input: IArchiveableInput) => void, object];
	buttonDisabled?: boolean;
}) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [archive] = useArchive();
	const [restore] = useRestore();

	const { t } = useTranslation();

	const toggleArchived = async () => {
		setIsLoading(true);

		if (archivableItem.isArchived) {
			await restore({
				variables: {
					input: {
						id: archivableItem.id,
					},
				},
			});
		} else {
			await archive({
				variables: {
					input: {
						id: archivableItem.id,
					},
				},
			});
		}

		setIsLoading(false);
	};

	return (
		<ActionListItem
			label={t(
				archivableItem.isArchived ? 'actions.list.restore.label' : 'actions.list.archive.label',
				{
					archivableItemName,
				},
			)}
			buttonTitle={t(archivableItem.isArchived ? 'common.restore' : 'common.archive')}
			buttonIndication={archivableItem.isArchived ? 'secondary' : 'destructive'}
			buttonDisabled={isLoading || buttonDisabled}
			onButtonClick={toggleArchived}
		/>
	);
};

export default ActionListItem;
